import { Component, computed, inject, signal } from "@angular/core";
import { RouterLink } from "@angular/router";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { Navbar } from "../../components/navbar/navbar";
import { Breadcrumb } from "../../components/breadcrumb/breadcrumb";
import { Footer } from "../../components/footer/footer";
import { SeoService } from "../../services/seo.service";
import { ROUTES } from "../../constants/routes";
import { SITE } from "../../constants/site";
import { ICONS } from "../../constants/icons";
import { ARTICLES_BY_DATE } from "../../constants/articles";
import {
  articleListSchema,
  breadcrumbSchema,
  webPageSchema
} from "../../helpers/structured-data";

@Component({
  selector: "app-resources",
  imports: [RouterLink, Breadcrumb, Navbar, Footer],
  templateUrl: "./resources.html",
  styleUrl: "./resources.scss"
})
export class Resources {
  readonly routes = ROUTES;
  readonly site = SITE;
  private readonly seo = inject(SeoService);
  private readonly sanitizer = inject(DomSanitizer);

  readonly allArticles = ARTICLES_BY_DATE;
  readonly featured = ARTICLES_BY_DATE[0];
  readonly activeCategory = signal<string>("All");

  readonly categories = ["All", ...new Set(ARTICLES_BY_DATE.map(item => item.category))];

  readonly visibleArticles = computed(() => {
    const category = this.activeCategory();
    const rest = this.allArticles.filter(item => item.slug !== this.featured.slug);
    return category === "All" ? rest : rest.filter(item => item.category === category);
  });

  setCategory(category: string): void {
    this.activeCategory.set(category);
  }

  iconFor(name: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(ICONS[name] ?? "");
  }

  formatDate(iso: string): string {
    return new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
      timeZone: "UTC"
    });
  }

  ngOnInit(): void {
    this.seo.apply({
      title: "Resources | Deceased Identity Fraud Guides and Research",
      description:
        "Practical guides on deceased identity fraud, death data quality, suppression workflows and compliance, written for bank, lender and enterprise fraud teams.",
      path: "/resources",
      keywords:
        "deceased identity fraud guide, death data resources, death master file limitations, deceased suppression workflow, death data compliance, fraud prevention research",
      structuredData: [
        breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Resources", path: "/resources" }
        ]),
        webPageSchema({
          name: "VerifyDeceased resource library",
          description:
            "Guides and research on deceased identity fraud, death data quality, suppression operations and compliance.",
          path: "/resources",
          type: "CollectionPage"
        }),
        articleListSchema(this.allArticles)
      ]
    });
  }
}
